import type { CpmAccountDisqualification } from '@/generated/account-disqualification';

export interface AccountBanUserGroup {
  userId: string;
  userName: string | null;
  bans: CpmAccountDisqualification[];
}

/**
 * The participants page's "with a disqualified account" view: one group per
 * clipper with at least one active (not lifted) account ban, built from the
 * campaign's full ban list rather than from one page of participants. Each
 * group keeps all of that clipper's bans (lifted ones too, so they stay
 * restorable), in list order. `search` matches the user name or an account
 * label, ignoring case and surrounding spaces.
 */
export function groupAccountBansByUser(
  bans: readonly CpmAccountDisqualification[],
  search = '',
): AccountBanUserGroup[] {
  const groups = new Map<string, AccountBanUserGroup>();
  for (const ban of bans) {
    const group = groups.get(ban.user_id);
    if (group) {
      group.bans.push(ban);
      group.userName ??= ban.user_name ?? null;
    } else {
      groups.set(ban.user_id, { userId: ban.user_id, userName: ban.user_name ?? null, bans: [ban] });
    }
  }
  const needle = search.trim().toLowerCase();
  return [...groups.values()]
    .filter(group => group.bans.some(ban => !ban.lifted_at))
    .filter(group => !needle
      || (group.userName ?? '').toLowerCase().includes(needle)
      || group.bans.some(ban => ban.account_label.toLowerCase().includes(needle)));
}
